import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";

export default function Navigation({ user, handleLogout }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("accueil");

  const navLinks = [
    { id: "accueil", label: "Accueil", href: "#top" },
    { id: "plans", label: "Forfaits", href: "#plans" },
    { id: "essai", label: "Séance d'essai", href: "#essai" },
    { id: "contact", label: "Contact", href: "#contact" },
  ];

  // Smooth scroll to section with offset for fixed header
  const scrollToSection = (href) => {
    if (href === "#top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const element = document.querySelector(href);
      if (element) {
        const headerOffset = 80; // Height of fixed header
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }
    setIsOpen(false); // Close mobile menu after click
  };

  // Detect active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = navLinks.map((link) => {
        if (link.href === "#top") return null;
        return {
          id: link.id,
          element: document.querySelector(link.href),
        };
      }).filter(Boolean);

      const scrollPosition = window.scrollY + 150; // Offset for better detection

      if (window.scrollY < 100) {
        setActiveSection("accueil");
        return;
      }

      for (const section of sections) {
        if (section.element) {
          const top = section.element.offsetTop;
          const bottom = top + section.element.offsetHeight;

          if (scrollPosition >= top && scrollPosition < bottom) {
            setActiveSection(section.id);
            return;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav className="sticky top-0 z-50 bg-black/90 backdrop-blur-sm border-b border-stone-800">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <div className="flex h-16 items-center justify-between">
          {/* Logo / Brand */}
          <button
            onClick={() => scrollToSection("#top")}
            className="text-xl font-bold text-white hover:text-blue-400 transition-colors"
          >
            Loulou Fitness
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.href)}
                className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                  activeSection === link.id
                    ? "text-white"
                    : "text-stone-400 hover:text-white"
                }`}
              >
                {link.label}
                {activeSection === link.id && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
                )}
              </button>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    className="px-4 py-2 text-sm font-medium text-white hover:text-blue-400 transition-colors"
                  >
                    Admin
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 rounded-full bg-stone-800 text-sm font-medium text-white hover:bg-stone-700 transition-colors"
                >
                  Déconnexion
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="px-4 py-2 rounded-full bg-blue-500 text-sm font-medium text-white hover:bg-blue-600 transition-colors"
              >
                Se connecter
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-white hover:text-blue-400 transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-stone-800">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.href)}
                  className={`px-4 py-3 text-left text-sm font-medium transition-colors ${
                    activeSection === link.id
                      ? "text-white bg-stone-800 border-l-2 border-blue-500"
                      : "text-stone-400 hover:text-white hover:bg-stone-900"
                  }`}
                >
                  {link.label}
                </button>
              ))}
              <div className="mt-4 px-4 flex flex-col gap-2">
                {user ? (
                  <>
                    {user.role === "admin" && (
                      <Link
                        to="/admin"
                        className="px-4 py-2 text-center rounded-full bg-stone-800 text-sm font-medium text-white hover:bg-stone-700 transition-colors"
                        onClick={() => setIsOpen(false)}
                      >
                        Admin
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsOpen(false);
                      }}
                      className="px-4 py-2 rounded-full bg-stone-800 text-sm font-medium text-white hover:bg-stone-700 transition-colors"
                    >
                      Déconnexion
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="px-4 py-2 text-center rounded-full bg-blue-500 text-sm font-medium text-white hover:bg-blue-600 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    Se connecter
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
